import { useCallback, useState } from 'react';

import { JWT } from 'domains/jwtToken/types';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import { selectAllChainsPaths } from 'modules/chains/store/selectors';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAddBlockchainsToWhitelistMutation } from 'domains/projects/actions/addBlockchainsToWhitelist';
import { useAppSelector } from 'store/useAppSelector';
import { useJWTsManager } from 'domains/jwtToken/hooks/useJWTsManager';
import { useProjectsWhitelistsBlockchains } from 'domains/projects/hooks/useProjectsWhitelistsBlockchains';

export interface IUseChangedProjectsProps {
  selectedSubchains: Record<string, string[]>;
}

export const useChangedProjects = ({
  selectedSubchains,
}: IUseChangedProjectsProps) => {
  const { group } = useAccount();

  const { jwts: projects } = useJWTsManager();
  const { projectsWhitelistsBlockchains } = useProjectsWhitelistsBlockchains({
    projects,
    group,
    skipFetching: true,
  });

  const allChainsPaths = useAppSelector(selectAllChainsPaths);

  const [addBlockchains, { isLoading: isLoadingAddChainsToProject }] =
    useAddBlockchainsToWhitelistMutation();

  const [isLoadingAddChainsToProjects, setIsLoadingAddChainsToProjects] =
    useState(false);

  const hasChanges = useCallback(
    (project: JWT) => {
      const projectBlockchains =
        projectsWhitelistsBlockchains.find(
          ({ userEndpointToken }) =>
            userEndpointToken === project.userEndpointToken,
        )?.blockchains ?? [];

      const currentChains = hasAllBlockchainsInWhitelist(projectBlockchains)
        ? allChainsPaths
        : projectBlockchains;

      const newSubchains = selectedSubchains[project.userEndpointToken];
      const hasNewSubchains = newSubchains.some(
        path => !currentChains.includes(path),
      );
      const hasRemovedSubchains = currentChains.some(
        path => !newSubchains.includes(path),
      );

      return hasNewSubchains || hasRemovedSubchains;
    },
    [allChainsPaths, projectsWhitelistsBlockchains, selectedSubchains],
  );

  const getChangedProjects = useCallback(async () => {
    const changedProjects: JWT[] = [];

    setIsLoadingAddChainsToProjects(true);

    try {
      await Promise.all(
        projects.map(async project => {
          if (!hasChanges(project)) return;

          changedProjects.push(project);

          const newChainPaths = selectedSubchains[project.userEndpointToken];

          const areAllChainsSelected =
            newChainPaths.length === allChainsPaths.length;

          await addBlockchains({
            params: {
              blockchains: areAllChainsSelected ? [] : newChainPaths,
              group,
              userEndpointToken: project.userEndpointToken,
            },
          });
        }),
      );
    } finally {
      setIsLoadingAddChainsToProjects(false);
    }

    return changedProjects;
  }, [
    addBlockchains,
    allChainsPaths,
    group,
    hasChanges,
    projects,
    selectedSubchains,
  ]);

  return {
    hasChanges,
    getChangedProjects,
    isLoadingAddChainsToProject,
    isLoadingAddChainsToProjects,
  };
};
